/**
 * The live editor's pure model: how a flow map document becomes React Flow
 * nodes and edges, and how a finished gesture on the canvas (a drag, a drop
 * into or out of a group, a group resize, a drawn or re-attached arrow, a new
 * node) becomes ONE document edit. Also the selection that the canvas, the
 * table and the inspector share.
 *
 * React-free, and @xyflow/react is imported for its TYPES only, so node:test
 * covers this file and nothing that imports it loads React Flow at runtime:
 * FlowMap.tsx reads the selection and placement rules from here while the
 * canvas itself stays in its lazily loaded chunk (FlowMapCanvas.tsx).
 */
import type { Edge, Node } from "@xyflow/react";
import { defaultHandles, edgeHandles, flowBounds, nodeBoxes, nodeSize } from "./layout.js";
import {
  FLOW_HANDLES,
  FLOW_NODE_SIZE,
  moveNodes,
  readText,
  setNodeParent,
  updateNode,
  type FlowEdge,
  type FlowEdgeKind,
  type FlowEdgeStyle,
  type FlowHandle,
  type FlowMapDoc,
  type FlowMapLabels,
  type FlowNode,
  type FlowNodeKind,
  type FlowNodeMark,
  type FlowPoint,
  type FlowSize,
} from "./model.js";

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/** What the editor has in focus: one node or one arrow. */
export interface FlowSelection {
  type: "node" | "edge";
  id: string;
}

export function sameSelection(a: FlowSelection | null | undefined, b: FlowSelection | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.type === b.type && a.id === b.id;
}

/** `sel` while its node or arrow is still in the map, else null (deleted, or undone away). */
export function liveSelection(doc: FlowMapDoc, sel: FlowSelection | null): FlowSelection | null {
  if (!sel) return null;
  const list: ReadonlyArray<{ id: string }> = sel.type === "node" ? doc.nodes : doc.edges;
  return list.some((x) => x.id === sel.id) ? sel : null;
}

/**
 * The one selection the inspector shows while the canvas has several things
 * selected: the current one as long as it is still among them, else the first
 * node, else the first arrow.
 */
export function primarySelection(
  nodeIds: readonly string[],
  edgeIds: readonly string[],
  current: FlowSelection | null,
): FlowSelection | null {
  if (current && (current.type === "node" ? nodeIds : edgeIds).includes(current.id)) return current;
  const n = nodeIds[0];
  if (n !== undefined) return { type: "node", id: n };
  const e = edgeIds[0];
  return e !== undefined ? { type: "edge", id: e } : null;
}

/** Mark exactly `id` as selected. Untouched items keep their identity; no change returns the same array. */
export function selectOnly<T extends { id: string; selected?: boolean }>(items: T[], id: string | null): T[] {
  let changed = false;
  const out = items.map((it) => {
    const want = it.id === id;
    if ((it.selected === true) === want) return it;
    changed = true;
    return { ...it, selected: want };
  });
  return changed ? out : items;
}

/** A node's title for lists, selects and screen readers, with the kind's fallback when it has none. */
export function nodeTitle(node: FlowNode, locale: string | undefined, labels: FlowMapLabels): string {
  const t = readText(node.title, locale).trim();
  if (t) return t;
  return node.kind === "group" ? labels.kindGroup : labels.untitled;
}

// ---------------------------------------------------------------------------
// Document -> React Flow
// ---------------------------------------------------------------------------

export type FlowNodeData = { node: FlowNode; locale?: string; labels: FlowMapLabels; editable: boolean; mark?: FlowNodeMark };
export type FlowRfNode = Node<FlowNodeData, FlowNodeKind>;

export type FlowEdgeData = { edge: FlowEdge; label: string; kind: FlowEdgeKind; edgeStyle: FlowEdgeStyle };
export type FlowRfEdge = Edge<FlowEdgeData, "flow">;

/** The one custom edge type the canvas registers. */
export const FLOW_EDGE_TYPE = "flow" as const;

/** The canvas's own arrow heads (coloured by CSS class, so they follow the theme). */
export const FLOW_MARKERS = ["arrow", "yes", "no", "selected"] as const;
export type FlowMarker = (typeof FLOW_MARKERS)[number];

export function markerOf(kind: FlowEdgeKind | undefined): FlowMarker {
  return kind === "yes" || kind === "no" ? kind : "arrow";
}

export function markerId(prefix: string, marker: FlowMarker): string {
  return `${prefix}-${marker}`;
}

/** The canvas camera (React Flow's viewport shape), kept by <FlowMap> across view switches. */
export interface FlowViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface RfBuildOptions {
  /** Language of the text on the cards and arrow labels. */
  locale?: string;
  labels: FlowMapLabels;
  editable: boolean;
  selection?: FlowSelection | null;
  /** Prefix of the canvas's arrow-head markers, unique per map on the page. */
  markerPrefix: string;
  /** The host's marks by node id (FlowMapProps.nodeMarks). */
  marks?: Readonly<Record<string, FlowNodeMark>>;
}

/**
 * React Flow nodes for the document, in document order (groups first, which
 * React Flow requires of parents). Node `type` is the kind; children keep
 * their group-relative position and `parentId`, with no extent, so a child can
 * be dragged out of its group. Groups carry their size as fixed dimensions (the
 * resizer edits those); other nodes get their kind's width and grow downwards.
 */
export function toRfNodes(doc: FlowMapDoc, o: RfBuildOptions): FlowRfNode[] {
  const selectedId = o.selection?.type === "node" ? o.selection.id : null;
  return doc.nodes.map((node) => {
    const size = nodeSize(node);
    const mark = o.marks?.[node.id];
    const rf: FlowRfNode = {
      id: node.id,
      type: node.kind,
      position: { x: node.position.x, y: node.position.y },
      data: { node, locale: o.locale, labels: o.labels, editable: o.editable, ...(mark ? { mark } : {}) },
      ariaLabel: nodeTitle(node, o.locale, o.labels),
      style: { width: size.width },
    };
    if (node.kind === "group") {
      rf.width = size.width;
      rf.height = size.height;
      rf.style = { width: size.width, height: size.height };
    }
    if (node.parentId) rf.parentId = node.parentId;
    if (node.id === selectedId) rf.selected = true;
    return rf;
  });
}

/** React Flow edges: every arrow on its (explicit or direction-default) handles, with a kind-coloured head. */
export function toRfEdges(doc: FlowMapDoc, o: RfBuildOptions): FlowRfEdge[] {
  const selectedId = o.selection?.type === "edge" ? o.selection.id : null;
  const titles = new Map(doc.nodes.map((n) => [n.id, nodeTitle(n, o.locale, o.labels)] as const));
  const edgeStyle: FlowEdgeStyle = doc.edgeStyle ?? "curved";
  return doc.edges.map((edge) => {
    const h = edgeHandles(doc, edge);
    const kind: FlowEdgeKind = edge.kind ?? "default";
    const label = readText(edge.label, o.locale).trim();
    const from = titles.get(edge.source) ?? o.labels.untitled;
    const to = titles.get(edge.target) ?? o.labels.untitled;
    const rf: FlowRfEdge = {
      id: edge.id,
      type: FLOW_EDGE_TYPE,
      source: edge.source,
      target: edge.target,
      sourceHandle: h.source,
      targetHandle: h.target,
      data: { edge, label, kind, edgeStyle },
      markerEnd: markerId(o.markerPrefix, markerOf(kind)),
      animated: false,
      ariaLabel: label ? `${from} → ${to}: ${label}` : `${from} → ${to}`,
    };
    if (edge.id === selectedId) rf.selected = true;
    return rf;
  });
}

const samePoint = (a: FlowPoint, b: FlowPoint): boolean => a.x === b.x && a.y === b.y;

function sameNode(p: FlowRfNode, n: FlowRfNode): boolean {
  return (
    p.type === n.type &&
    p.data.node === n.data.node &&
    p.data.locale === n.data.locale &&
    p.data.labels === n.data.labels &&
    p.data.editable === n.data.editable &&
    p.data.mark === n.data.mark &&
    p.parentId === n.parentId &&
    samePoint(p.position, n.position) &&
    p.width === n.width &&
    p.height === n.height &&
    p.ariaLabel === n.ariaLabel
  );
}

function sameEdge(p: FlowRfEdge, n: FlowRfEdge): boolean {
  const a = p.data;
  const b = n.data;
  return (
    !!a &&
    !!b &&
    a.edge === b.edge &&
    a.label === b.label &&
    a.kind === b.kind &&
    a.edgeStyle === b.edgeStyle &&
    p.source === n.source &&
    p.target === n.target &&
    p.sourceHandle === n.sourceHandle &&
    p.targetHandle === n.targetHandle &&
    p.markerEnd === n.markerEnd &&
    p.ariaLabel === n.ariaLabel
  );
}

function mergeBy<T extends { id: string; selected?: boolean }>(
  prev: readonly T[],
  next: T[],
  same: (p: T, n: T) => boolean,
  carry: (p: T, n: T) => T,
): T[] {
  const byId = new Map(prev.map((x) => [x.id, x] as const));
  let identical = prev.length === next.length;
  const out = next.map((n, i) => {
    const p = byId.get(n.id);
    if (!p) {
      identical = false;
      return n;
    }
    if (same(p, n)) {
      if (prev[i] !== p) identical = false;
      return p;
    }
    identical = false;
    return carry(p, n);
  });
  return identical ? (prev as T[]) : out;
}

/**
 * Fold freshly built nodes into the canvas's current ones. An unchanged node
 * keeps its object (React Flow then skips it); a changed one keeps its measured
 * size (no hidden flash while it is measured again) and the canvas's own
 * selection flag, which may cover several nodes. Nothing changed returns `prev`.
 */
export function mergeRfNodes(prev: readonly FlowRfNode[], next: FlowRfNode[]): FlowRfNode[] {
  return mergeBy(prev, next, sameNode, (p, n) => {
    const out: FlowRfNode = { ...n };
    if (p.measured) out.measured = p.measured;
    if (p.selected) out.selected = true;
    else delete out.selected;
    return out;
  });
}

/** The same fold for edges. */
export function mergeRfEdges(prev: readonly FlowRfEdge[], next: FlowRfEdge[]): FlowRfEdge[] {
  return mergeBy(prev, next, sameEdge, (p, n) => {
    const out: FlowRfEdge = { ...n };
    if (p.selected) out.selected = true;
    else delete out.selected;
    return out;
  });
}

// ---------------------------------------------------------------------------
// Canvas gestures -> document edits
// ---------------------------------------------------------------------------

const isHandle = (v: unknown): v is FlowHandle => typeof v === "string" && (FLOW_HANDLES as readonly string[]).includes(v);

/**
 * The handles to store for an arrow drawn (or re-attached) on the canvas:
 * nothing when they are the map direction's defaults, so a later auto layout or
 * direction change still turns the arrow; both when the author picked other sides.
 */
export function connectionHandles(
  doc: FlowMapDoc,
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined,
): { sourceHandle?: FlowHandle; targetHandle?: FlowHandle } {
  const d = defaultHandles(doc.direction);
  const s = isHandle(sourceHandle) ? sourceHandle : d.source;
  const t = isHandle(targetHandle) ? targetHandle : d.target;
  return s === d.source && t === d.target ? {} : { sourceHandle: s, targetHandle: t };
}

/**
 * After nodes were moved: each moved node joins the group its box centre now
 * sits in (the smallest one when groups overlap), or leaves its group when the
 * centre is outside every group. It keeps its place on the canvas either way.
 * Groups never join groups. `sizes` holds measured boxes (cards grow with their
 * content); without it the kind's box is used.
 */
export function regroupMoved(doc: FlowMapDoc, ids: Iterable<string>, sizes?: ReadonlyMap<string, FlowSize>): FlowMapDoc {
  const groups = doc.nodes.filter((n) => n.kind === "group");
  const boxes = nodeBoxes(doc);
  let next = doc;
  for (const id of new Set(ids)) {
    const node = doc.nodes.find((n) => n.id === id);
    const box = boxes.get(id);
    if (!node || !box || node.kind === "group") continue;
    const size = sizes?.get(id);
    const cx = box.x + (size?.width ?? box.width) / 2;
    const cy = box.y + (size?.height ?? box.height) / 2;
    let target: { id: string; area: number } | null = null;
    for (const g of groups) {
      const b = boxes.get(g.id);
      if (!b || cx < b.x || cx > b.x + b.width || cy < b.y || cy > b.y + b.height) continue;
      const area = b.width * b.height;
      if (!target || area < target.area) target = { id: g.id, area };
    }
    next = setNodeParent(next, id, target?.id ?? null);
  }
  return next;
}

/**
 * A group resized on the canvas: its new box, with its children shifted back by
 * however far the group's corner moved, so they stay where they were on screen
 * (React Flow does the same while the resize is live).
 */
export function resizeGroup(doc: FlowMapDoc, id: string, rect: { x: number; y: number; width: number; height: number }): FlowMapDoc {
  const before = doc.nodes.find((n) => n.id === id);
  if (!before || before.kind !== "group") return doc;
  const next = updateNode(doc, id, { position: { x: rect.x, y: rect.y }, size: { width: rect.width, height: rect.height } });
  const after = next.nodes.find((n) => n.id === id);
  if (!after) return next;
  const dx = after.position.x - before.position.x;
  const dy = after.position.y - before.position.y;
  if (!dx && !dy) return next;
  const moves = next.nodes
    .filter((n) => n.parentId === id)
    .map((n) => ({ id: n.id, position: { x: n.position.x - dx, y: n.position.y - dy } }));
  return moves.length ? moveNodes(next, moves) : next;
}

/** The canvas's snap grid. */
export const FLOW_GRID = 8;
/** Space between the lowest node and a node added below it. */
const NEW_NODE_GAP = 48;
/** How far a new node steps aside from one already sitting on the same spot. */
const NUDGE = 24;

const snap = (n: number): number => Math.round(n / FLOW_GRID) * FLOW_GRID;

/**
 * Where a new node of `kind` goes: centred on `centre` (the middle of the
 * visible canvas) when given, else under the lowest node (the table view), else
 * at the origin. Snapped to the grid, and stepped diagonally off any top-level
 * node that already sits on exactly that spot, so repeated adds do not stack.
 */
export function placeNewNode(doc: FlowMapDoc, kind: FlowNodeKind, centre?: FlowPoint | null): FlowPoint {
  const size = FLOW_NODE_SIZE[kind] ?? FLOW_NODE_SIZE.step;
  let p: FlowPoint;
  if (centre && Number.isFinite(centre.x) && Number.isFinite(centre.y)) {
    p = { x: snap(centre.x - size.width / 2), y: snap(centre.y - size.height / 2) };
  } else if (doc.nodes.length === 0) {
    p = { x: 0, y: 0 };
  } else {
    const b = flowBounds(doc);
    p = { x: snap(b.x + b.width / 2 - size.width / 2), y: snap(b.y + b.height + NEW_NODE_GAP) };
  }
  const taken = new Set(doc.nodes.filter((n) => !n.parentId).map((n) => `${n.position.x},${n.position.y}`));
  for (let i = 0; i < 40 && taken.has(`${p.x},${p.y}`); i++) p = { x: p.x + NUDGE, y: p.y + NUDGE };
  return { x: p.x === 0 ? 0 : p.x, y: p.y === 0 ? 0 : p.y };
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

/**
 * Web links open in a new tab. App paths, anchors and queries stay in the page,
 * and so do mail and phone links (they hand off to another app; a new tab would
 * only leave an empty one behind).
 */
export function opensInNewTab(href: string): boolean {
  return /^https?:/i.test(href);
}
